import { createSelector, createSlice } from "@reduxjs/toolkit";
import { getAmountInvestedAction, getSharesPurchasedAction, getTickersAction } from "../../actions";
import Instrument from "../../entity/Instrument";
import { sortDataByDate } from "../../utils";
import { instrumentSettingsIndexer, setSettings } from "./settings";

const initialState = {
  instruments: {},
  getInstruments: {
    ids: [],
    loading: undefined,
    refreshing: undefined,
    fetchError: null,
    refreshError: null,
  },
  selectedInstrumentId: undefined,
  amountInvested: {},
  sharePurchased: {},
  dividendData: null,
  shareData: {},
  shareMountQuery: {},
  entryAmountInvested: {
    loading: undefined,
    refreshing: undefined,
    fetchError: null,
    refreshError: null,
  },
  entrySharePurchased: {
    loading: undefined,
    refreshing: undefined,
    fetchError: null,
    refreshError: null,
  },
  userEntryAmountInvested: undefined,
  selectedInstrumentMinimumPrice: undefined,
};
const tradeSlice = createSlice({
  name: "trade",
  initialState,
  reducers: {
    setSelectedInstrument: (state, { payload }) => {
      state.selectedInstrumentId = payload;
    },
    updateQuery: (state, { payload }) => {
      state.shareMountQuery = { ...state.shareMountQuery, ...payload };
    },
    updateUserEntryAmountInvested: (state, { payload }) => {
      state.userEntryAmountInvested = payload;
    },
    updateSelectedInstrumentMinimumPrice: (state, { payload }) => {
      state.selectedInstrumentMinimumPrice = payload;
    }
  },
  extraReducers: builder => {
    builder.addCase(getTickersAction.pending, (state, action) => {
      state.getInstruments.loading = true;
    });
    builder.addCase(getTickersAction.fulfilled, (state, { payload }) => {
      state.instruments = payload.data.instruments.reduce(
        (s, inst) => ({ ...s, [inst.instrumentId]: inst }),
        {}
      );
      state.getInstruments.ids = payload.data.instruments.map(inst => inst.instrumentId);
      state.getInstruments.loading = false;
    });
    builder.addCase(getTickersAction.rejected, (state, action) => {
      state.fetchError = action.error;
      state.getInstruments.loading = false;
    });
    builder.addCase(setSettings.type, (state, { payload }) => {
      state.selectedInstrumentId =
        payload.instruments.find(ins => !!ins.default)?.id || payload.instruments[0]?.id;
    });

    builder.addCase(getAmountInvestedAction.pending, state => {
      state.entryAmountInvested.loading = true;
    });
    builder.addCase(getAmountInvestedAction.fulfilled, (state, { payload }) => {
      const { data, dividendData, shareData } = payload.data.amountInvested;
      state.shareMountQuery = payload.query;
      /**
       * Separates amountInvested state and sharePurchased state because current time those state is same, but in the future those maybe light different
       */
      state.amountInvested = data;
      state.sharePurchased = data;
      state.dividendData = dividendData
        ? { ...dividendData, datas: sortDataByDate(dividendData.datas) }
        : dividendData;
      state.shareData = { ...shareData, datas: sortDataByDate(shareData.datas) };
      state.entryAmountInvested.loading = false;
    });
    builder.addCase(getAmountInvestedAction.rejected, (state, { payload }) => {
      state.entryAmountInvested.fetchError = payload;
      state.entryAmountInvested.loading = false;
    });

    builder.addCase(getSharesPurchasedAction.pending, state => {
      state.entrySharePurchased.loading = true;
    });
    builder.addCase(getSharesPurchasedAction.fulfilled, (state, { payload }) => {
      const { data, dividendData, shareData } = payload.data.shareBought;
      state.shareMountQuery = payload.query;
      state.amountInvested = data;
      state.sharePurchased = data;
      state.dividendData = dividendData
        ? { ...dividendData, datas: sortDataByDate(dividendData.datas) }
        : dividendData;
      state.shareData = { ...shareData, datas: sortDataByDate(shareData.datas) };
      state.entrySharePurchased.loading = false;
    });
    builder.addCase(getSharesPurchasedAction.rejected, (state, { payload }) => {
      state.entrySharePurchased.fetchError = payload;
      state.entrySharePurchased.loading = false;
    });
  },
});

export default tradeSlice;

export const instrumentSelector = createSelector(
  [
    state => state.trade.getInstruments.ids.map(id => state.trade.instruments[id]),
    state => state.trade.getInstruments.loading,
    state => state.trade.getInstruments.error,
  ],
  (instruments, loading, error) => ({
    instruments,
    loading,
    error,
  })
);

export const selectedInstrumentSelector = createSelector(
  [state => state.trade.selectedInstrumentId, state => state.trade.instruments],
  (selectedInstrumentId, instruments) => new Instrument(instruments[selectedInstrumentId])
);

export const selectedInstrumentMinimumPriceSelector = createSelector(
  [state => state.trade.selectedInstrumentMinimumPrice],
  (selectedInstrumentMinimumPrice) => selectedInstrumentMinimumPrice
);

export const amountInvestedStateSelectorWrapper = () =>
  createSelector(
    [
      state => state.trade.amountInvested,
      state => state.trade.entryAmountInvested,
      state => state.trade.shareMountQuery,
      state => state.trade.userEntryAmountInvested,
    ],
    (amountInvested, amountInvestedState, query, userEntryAmountInvested) => ({
      amountInvested,
      ...amountInvestedState,
      query,
      userEntryAmountInvested,
    })
  );

export const sharePurchasedSelectorWrapper = () =>
  createSelector(
    [
      state => state.trade.sharePurchased,
      state => state.trade.entrySharePurchased,
      state => state.trade.shareMountQuery,
    ],
    (sharePurchased, sharePurchasedState, query) => ({
      sharePurchased,
      ...sharePurchasedState,
      query,
    })
  );
export const amountInvestedSelector = state => state.trade.amountInvested;
export const shareDataSelector = state => state.trade.shareData;
export const dividendDataSelector = state => state.trade.dividendData;
export const instrumentsIndexerSelector = (/** @type { RootState }  */ state) => state.trade.instruments;

/**
 * instrument settings and instrument data (loaded by another api) depend together, we merge both to one to easy select and get data or custom field
 * eg: we want select instrument name and custom field color one time, color is the custom field in setting, instrument name in SData response
 * we need select both to get it.
 *
 * @typedef { Record<number, {
 * setting: import("../../..").UnWrapRecord<ReturnType<typeof instrumentSettingsIndexer>>,
 * data: import("../../..").UnWrapRecord<ReturnType<typeof instrumentsIndexerSelector>>
 * }> } IInstrumentDataAndSettingSelectorReturn
 */
export const instrumentDataAndSettingSelector = createSelector(
  [instrumentsIndexerSelector, instrumentSettingsIndexer],
  (data, settings) => {
    return Object.keys(settings).reduce((acc, key) => {
      acc[key] = {
        setting: settings[key],
        data: data[key] || {},
      };
      return acc;
    }, /** @type { IInstrumentDataAndSettingSelectorReturn } */ ({}));
  }
);

export const { setSelectedInstrument, updateQuery, updateUserEntryAmountInvested, updateSelectedInstrumentMinimumPrice } = tradeSlice.actions;
